/**
 * Wave 4.07 — CAD Developers master page. CRUD list for the CAD Developer
 * party type (the studio/person who develops the 3D CAD for an order).
 * Modeled after the Cutters master page. Access is gated by
 * ``cad_developers.view`` (+ create / edit sub-keys enforced by the backend).
 */
import React, { useEffect, useState } from 'react';
import AppShell from '@/components/AppShell';
import CompanyGate from '@/components/CompanyGate';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, PencilRuler } from 'lucide-react';
import { formatDate } from '@/lib/format';
import { extractError } from '@/lib/errors';
import { CadDeveloperFormDialog } from '@/components/cad/CadDeveloperFormDialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export default function CADDevelopers() {
  const { effectiveCompanyId } = useAuth();
  return (
    <AppShell
      title="CAD Developers"
      subtitle="Master list of CAD developers. They design the 3D CAD for an order and can be attributed on the CAD production step."
    >
      <CompanyGate>
        <CADDevelopersContent companyId={effectiveCompanyId} />
      </CompanyGate>
    </AppShell>
  );
}

function CADDevelopersContent({ companyId }) {
  const [list, setList] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [delTarget, setDelTarget] = useState(null);

  const load = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const { data } = await api.get('/masters/cad-developers', { params: { company_id: companyId } });
      setList(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(extractError(e, 'Failed to load CAD developers'));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [companyId]);

  const filtered = list.filter((c) => {
    if (!query.trim()) return true;
    const q = query.trim().toLowerCase();
    return (
      (c.name || '').toLowerCase().includes(q) ||
      (c.phone || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q) ||
      (c.notes || '').toLowerCase().includes(q)
    );
  });

  const confirmDelete = async () => {
    if (!delTarget) return;
    try {
      const { data } = await api.delete(`/masters/cad-developers/${delTarget.id}`);
      if (data?.archived) {
        toast.success('CAD Developer archived — it is referenced by existing orders.');
      } else {
        toast.success('CAD Developer deleted');
      }
      setDelTarget(null);
      load();
    } catch (e) {
      toast.error(extractError(e, 'Delete failed'));
    }
  };

  return (
    <div className="space-y-4">
      <div className="sticky-filter-bar flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg px-3" data-testid="cad-developers-filter-bar">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-2 top-2.5 text-slate-400" />
          <Input
            className="pl-8"
            placeholder="Search by name, phone, email or notes…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="cad-developers-search"
          />
        </div>
        <Button
          onClick={() => { setEditing(null); setOpen(true); }}
          data-testid="cad-developers-add-btn"
        >
          <Plus size={14} className="mr-1" /> Add CAD Developer
        </Button>
      </div>

      <Card className="p-0 overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-2.5 text-[11px] uppercase tracking-wider text-slate-500 border-b border-[var(--divider)]">
          <div className="col-span-3">Name</div>
          <div className="col-span-2">Phone</div>
          <div className="col-span-3">Email</div>
          <div className="col-span-2">Notes</div>
          <div className="col-span-1">Added</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        {loading && (
          <div className="px-4 py-6 text-sm text-slate-500">Loading…</div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-slate-500">
            <PencilRuler className="mx-auto mb-2" size={22} />
            No CAD developers yet. Click <b>Add CAD Developer</b> to create one.
          </div>
        )}
        {filtered.map((c) => (
          <div
            key={c.id}
            className="grid grid-cols-12 px-4 py-3 items-center border-b border-[var(--divider)] hover:bg-slate-50"
            data-testid={`cad-developer-row-${c.id}`}
          >
            <div className="col-span-3 font-medium text-[var(--ink-navy)]">{c.name}</div>
            <div className="col-span-2 text-sm text-slate-600 font-mono tabular-nums">{c.phone || '—'}</div>
            <div className="col-span-3 text-sm text-slate-600 truncate">{c.email || '—'}</div>
            <div className="col-span-2 text-sm text-slate-600 truncate" title={c.notes || ''}>{c.notes || '—'}</div>
            <div className="col-span-1 text-[11px] text-slate-500">{formatDate(c.created_at)}</div>
            <div className="col-span-1 flex justify-end gap-1">
              <Button
                size="icon" variant="ghost"
                onClick={() => { setEditing(c); setOpen(true); }}
                data-testid={`cad-developer-edit-${c.id}`}
              >
                <Pencil size={14} />
              </Button>
              <Button
                size="icon" variant="ghost"
                onClick={() => setDelTarget(c)}
                data-testid={`cad-developer-delete-${c.id}`}
              >
                <Trash2 size={14} className="text-rose-600" />
              </Button>
            </div>
          </div>
        ))}
      </Card>

      <CadDeveloperFormDialog
        open={open}
        onOpenChange={setOpen}
        developer={editing}
        companyId={companyId}
        onSaved={load}
      />

      <AlertDialog open={Boolean(delTarget)} onOpenChange={(v) => !v && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete CAD Developer</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <b>{delTarget?.name}</b>? This cannot be undone.
              If the developer is already referenced by one or more orders, it will be archived
              instead so historical CAD attributions survive in the report.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-rose-600 hover:bg-rose-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
